import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionButton, Content, Dialog, DialogTrigger, Divider, Flex, Heading } from '@adobe/react-spectrum';
import './InfoDialog.css';
import '../../common/searedDialog/searedDialog.css';
import { getAnalytics, logEvent } from 'firebase/analytics';
import isMobile from 'ismobilejs';
import { FEATURE_FLAGS } from '../../../config/featureFlags';
import { resurfaceConsent } from '../../../utils/consent';

const mobile = isMobile(window.navigator).phone;

// We point at the shared `/resources/saltshaker.svg` asset and flip it
// horizontally via CSS — the existing art already matches the site look.

const handleDiscordLink = async () => {
    logEvent(getAnalytics(), 'discord_click');
    window.open(`https://discord.gg/Yb2CAxcJcf`, `_blank`);
};
const handlePatreonLink = async () => {
    logEvent(getAnalytics(), 'patreon_click');
    window.open(
        `https://patreon.com/user?u=4796225&utm_medium=clipboard_copy&utm_source=copyLink&utm_campaign=creatorshare_creator&utm_content=join_link`,
        `_blank`
    );
};

export function InfoDialog() {
    const navigate = useNavigate();

    // Compliance links. "Privacy Policy" is always shown (gated by privacyPolicy,
    // which is always on) since the policy must be live before ads. "Do Not Sell"
    // and "Consent Settings" stay behind the ads flag: they try to reopen
    // NitroPay's CMP, and until that script is installed there's no CMP, so we
    // fall back to the relevant privacy section.
    const goPrivacy = (close, hash = '') => {
        close();
        navigate(`/privacy${hash}`);
    };
    const handlePrivacyChoice = (close, hash) => {
        if (!resurfaceConsent()) {
            goPrivacy(close, hash);
        } else {
            close();
        }
    };

    // Kick off a shake every ~30s so the footer feels alive. We add the
    // class for a beat, let the keyframes run, then remove it — the
    // component re-arms the timer via the same interval.
    const [shaking, setShaking] = useState(false);
    useEffect(() => {
        const SHAKE_MS = 900;
        const INTERVAL_MS = 30000;
        const handle = setInterval(() => {
            setShaking(true);
            setTimeout(() => setShaking(false), SHAKE_MS);
        }, INTERVAL_MS);
        return () => clearInterval(handle);
    }, []);

    return (
        <DialogTrigger isDismissable>
            <ActionButton
                aria-label="Info"
                isQuiet={true}
                UNSAFE_className={`infoTerminalButton${shaking ? ' infoTerminalButton--shaking' : ''}`}
                onPress={() => {
                    logEvent(getAnalytics(), 'info_click');
                }}
            >
                <Flex direction={'row'}>
                    <span className="infoLabel">Info</span>
                    <span className="infoTerminalSalt">
                        <img
                            src="/resources/saltshaker-footerbar.svg"
                            alt=""
                            className="saltShakerSvg"
                            aria-hidden="true"
                        />
                        <span className="saltShakerGrains" aria-hidden="true">
                            <span className="saltShakerGrain saltShakerGrain--1" />
                            <span className="saltShakerGrain saltShakerGrain--2" />
                            <span className="saltShakerGrain saltShakerGrain--3" />
                            <span className="saltShakerGrain saltShakerGrain--4" />
                            <span className="saltShakerGrain saltShakerGrain--5" />
                        </span>
                    </span>
                </Flex>
            </ActionButton>
            {(close) => (
                <Dialog padding={0} width="calc(100vw - 40px)" maxWidth={540} UNSAFE_className="searedDialog">
                    <Heading>
                        <span className="searedDialog__eyebrow">About</span>
                        <span className="searedDialog__title">Commandersalt.com</span>
                        <span className="searedDialog__subtitle">
                            a hobby project with an unhealthy level of time commitment
                        </span>
                    </Heading>
                    <Divider />
                    <Content
                        UNSAFE_style={{
                            height: mobile ? 520 : 380,
                            maxHeight: 'calc(100vh - 10px)',
                        }}
                    >
                        <div className="searedDialog__body">
                            <p className="searedDialog__paragraph">
                                This site is a hobby project I pay for out of pocket. I'm trying to keep it free of ads,
                                but that gets harder as the site grows. If you'd like to help keep the dream alive,
                                consider joining the Patreon.
                            </p>
                            <p className="searedDialog__paragraph">
                                Questions, comments, feature requests? The Discord is the fastest way to reach us.
                                Manuel — our resident basement-dwelling AI — will happily roast your decks over there.
                            </p>
                            <Flex
                                direction="row"
                                justifyContent="center"
                                gap="size-250"
                                UNSAFE_className="searedDialog__actions"
                            >
                                <ActionButton
                                    onPress={handlePatreonLink}
                                    aria-label="Support us on Patreon!"
                                    isQuiet={true}
                                    UNSAFE_className="searedDialog__iconBtn"
                                >
                                    <img src="/resources/patreon.png" alt="Patreon" />
                                </ActionButton>
                                <ActionButton
                                    onPress={handleDiscordLink}
                                    aria-label="Join our Discord!"
                                    isQuiet={true}
                                    UNSAFE_className="searedDialog__iconBtn"
                                >
                                    <img src="/resources/discord.png" alt="Discord" />
                                </ActionButton>
                            </Flex>
                            {(FEATURE_FLAGS.privacyPolicy || FEATURE_FLAGS.ads) && (
                                <div className="searedDialog__compliance">
                                    {FEATURE_FLAGS.privacyPolicy && (
                                        <button
                                            type="button"
                                            className="searedDialog__complianceLink"
                                            onClick={() => goPrivacy(close)}
                                        >
                                            Privacy Policy
                                        </button>
                                    )}
                                    {FEATURE_FLAGS.ads && (
                                        <>
                                            <button
                                                type="button"
                                                className="searedDialog__complianceLink"
                                                onClick={() => handlePrivacyChoice(close, '#ccpa')}
                                            >
                                                Do Not Sell or Share My Personal Information
                                            </button>
                                            <button
                                                type="button"
                                                className="searedDialog__complianceLink"
                                                onClick={() => handlePrivacyChoice(close, '#consent')}
                                            >
                                                Cookie &amp; Consent Settings
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                            <div className="searedDialog__legal">
                                <b>Obligatory legal shiz:</b> Wizards of the Coast, Magic: The Gathering, and their
                                logos are trademarks of Wizards of the Coast LLC in the United States and other
                                countries. © 1993-2026 Wizards. All Rights Reserved. Commandersalt is not affiliated
                                with, endorsed, sponsored, or specifically approved by Wizards of the Coast LLC.
                                Commandersalt may use the trademarks and other intellectual property of Wizards of the
                                Coast LLC, which is permitted under Wizards' Fan Site Policy. MAGIC: THE GATHERING® is a
                                trademark of Wizards of the Coast. For more information, visit
                                https://company.wizards.com/.
                            </div>
                        </div>
                    </Content>
                </Dialog>
            )}
        </DialogTrigger>
    );
}
